/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';

import { PageRequest } from '@/core/request/PageRequest';

import { DEFAULT_ROWS } from '..';
import {
  alterStatusInventory,
  createInventory,
  createItemInventory,
  getInventory,
  listAllItemsInventory,
  listInventories,
  listItemsInventory,
  nextCountInventory,
  removeInventory,
  ResetUnmarkedItemsInventory,
  updateAllInventory,
  updateInventory,
  updateItemInventory,
  ValidateInventory,
} from './inventory.service';
import {
  AlterStatusInventory,
  CreateInventoryRequest,
  InventoryItemListResponse,
  InventoryItemsResponse,
  LaunchItemsRequest,
  ListItemInventoryParams,
  ListItemInventoryResponse,
  UpdateInventoryRequest,
} from './Inventory.types';
import { notification } from 'antd';
import { useIntl } from 'react-intl';
import notificationError from '@/utils/functions/notificationError';
import { PageResult } from '@/core/request/PageResult';
import { queryClient } from '@/Root';
import { StockItem } from '../../stock/StockItems/StockItems.types';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';

const errors: Record<string, string> = {
  'Cannot add items to a inventory that is not in progress':
    'Não é possível adicionar items ao inventário que está finalizado.',
  'Product already exists. Use update services instead':
    'O produto já está no seu inventario.',
};

export function useInventories(params?: PageRequest) {
  const query = useInfiniteQuery(
    ['inventories', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listInventories(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useCreateInventory() {
  const intl = useIntl();

  const mutation = useMutation(
    (obj: CreateInventoryRequest) => createInventory(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'inventory.create.notifications.success.title',
          }),
          description: intl.formatMessage({
            id: 'inventory.create.notifications.success.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useInventory(id: number) {
  const query = useQuery(['inventories', id], () => getInventory(id));

  useServiceError(query.error);

  return query;
}

export function useRemoveInventory() {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const mutation = useMutation((id: number) => removeInventory(id), {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'inventory.notifications.remove.success.title',
        }),
        description: intl.formatMessage({
          id: 'inventory.notifications.remove.success.description',
        }),
      });
      queryClient.invalidateQueries(['inventories']);
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateInventory() {
  const intl = useIntl();

  const mutation = useMutation(
    (obj: UpdateInventoryRequest) => updateInventory(obj),
    {
      onSuccess() {
        notification.success({
          message: intl.formatMessage({
            id: 'inventory.update.notification.success',
          }),
          description: intl.formatMessage({
            id: 'inventory.update.notification.successDescription',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

// Listagem dos itens do inventário

export function useItemsInventory(
  id: number,
  params?: ListItemInventoryParams,
) {
  const query = useQuery(
    ['itemsInventory', { ...params }],
    () => {
      const req: ListItemInventoryParams = {
        ...params,
        rows: params?.rows || DEFAULT_ROWS,
      };

      return listItemsInventory(id, req);
    },
    { enabled: !!id, cacheTime: 5000 },
  );

  useServiceError(query.error);

  return query;
}

export function useAllItemsInventory(id: number, params?: PageRequest) {
  const query = useQuery(
    ['allInventory', id, { ...params }],
    () => listAllItemsInventory(id, params),
    { enabled: !!id },
  );

  return query;
}

export function updateQueryData(data: InventoryItemsResponse, isEdit = true) {
  return (oldData?: PageResult<ListItemInventoryResponse>) => {
    if (isEdit) {
      return {
        nextToken: oldData?.nextToken || '',
        data:
          oldData?.data.map((item) =>
            item.item?.id === data.id ? { ...item, item: data } : item,
          ) || [],
      };
    }

    return {
      nextToken: oldData?.nextToken || '',
      data:
        oldData?.data.map((item) =>
          item.stockItemsDTO.partId === data.partId
            ? { ...item, item: data }
            : item,
        ) || [],
    };
  };
}

export function useCreateItemInventory(
  inventoryId: number,
  params?: ListItemInventoryParams,
) {
  const queryClient = useQueryClient();

  return useMutation(
    (obj: LaunchItemsRequest) => createItemInventory(obj, inventoryId),
    {
      async onSuccess() {
        await queryClient.invalidateQueries(['itemsInventory', { ...params }]);

        notification.success({ message: 'Atualização concluída!' });
      },

      onError(error) {
        notificationError(error, {
          translate: (errs) => errs.map((err) => errors[err]),
        });
      },
    },
  );
}

export function useUpdateItemInventory(
  inventoryId: number,
  params?: ListItemInventoryParams,
) {
  const queryClient = useQueryClient();

  return useMutation(
    (obj: LaunchItemsRequest) => updateItemInventory(obj, inventoryId),
    {
      async onSuccess() {
        await queryClient.invalidateQueries(['itemsInventory', { ...params }]);

        notification.success({ message: 'Atualização concluída!' });
      },
      onError(error) {
        notificationError(error, {
          translate: (errs) => errs.map((err) => errors[err]),
        });
      },
    },
  );
}

export function useUpdateAllInventory(inventoryId?: number) {
  return useMutation(
    async (obj: { items: LaunchItemsRequest[]; stockItems: StockItem[] }) => {
      return updateAllInventory(obj.items, inventoryId);
    },
    {
      async onSuccess(data, vars) {
        const query = queryClient
          .getQueriesData<InventoryItemListResponse>(['itemsInventory'])
          .at(0);

        if (query && query.length && query[1]?.data.length) {
          const totalItems = vars.items.length;

          queryClient.setQueryData<InventoryItemListResponse>(
            query[0],
            (oldData) => {
              let pagedPartsIds = vars.items
                .filter((item) => item.partId)
                .map((item) => item.partId)
                .join(',');

              let pagedConsumablesIds = vars.items
                .filter((item) => item.consumableId)
                .map((item) => item.consumableId)
                .join(',');

              if (pagedPartsIds) {
                pagedPartsIds = pagedPartsIds + ',';
              }

              if (pagedConsumablesIds) {
                pagedConsumablesIds = pagedConsumablesIds + ',';
              }

              const rows =
                (Array.isArray(query[0]) && query[0][1]?.rows) || DEFAULT_ROWS;

              const pageData = [
                ...data
                  .map((item) => {
                    const stockItem = vars.stockItems.find(
                      (stockItem) =>
                        stockItem.partId === item.partId ||
                        stockItem.consumableId === item.consumableId,
                    );

                    return {
                      item,
                      stockItemsDTO: stockItem,
                    } as any;
                  })
                  .reverse(),
                ...(oldData?.data.slice(
                  0,
                  totalItems < rows ? rows - totalItems : 0,
                ) || []),
              ].slice(0, rows);

              return oldData?.data.length
                ? {
                    ...oldData,
                    data: pageData,
                    nextToken:
                      oldData?.nextToken ||
                      (pageData.length === rows ? String(rows) : undefined),
                    currentPage: 1,
                    itemsCount: oldData.itemsCount + totalItems,
                    pagedPartsIds: pagedPartsIds + oldData.pagedPartsIds,
                    pagedConsumablesIds:
                      pagedConsumablesIds + oldData.pagedConsumablesIds,
                  }
                : ({} as any);
            },
          );
        } else {
          await queryClient.invalidateQueries(['itemsInventory']);
        }

        notification.success({ message: 'Atualização concluída!' });
      },
      onError(error) {
        notificationError(error, {
          translate: (errs) => errs.map((err) => errors[err] || err),
        });
      },
    },
  );
}

export function useAlterStatusInventory() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: AlterStatusInventory) => alterStatusInventory(obj),
    {
      onSuccess(_, data) {
        notification.success({
          message: intl.formatMessage({
            id: 'inventory.finalize.notification.success.title',
          }),
          description: intl.formatMessage({
            id: 'inventory.finalize.notification.success.description',
          }),
        });

        queryChanel({
          invalidateQueries: [['allInventory', data.id], ['inventories']],
        });
      },
      onError(error) {
        notificationError(error, {
          translate: (errors) =>
            errors.map((err) =>
              err.includes('Inventory with no items')
                ? 'Estoque sem itens'
                : err,
            ),
        });
      },
    },
  );

  return mutation;
}

export function useResetUnmarkedItemsInventory() {
  const queryChanel = useQueryChanel();

  return useMutation(ResetUnmarkedItemsInventory, {
    onSuccess(_, inventoryId) {
      notification.success({
        message: 'Sucesso',
        description: 'Itens não marcados zerados com sucesso',
      });

      queryChanel({
        invalidateQueries: [
          ['inventories'],
          ['itemsInventory'],
          ['inventories', inventoryId],
          ['allInventory', inventoryId],
        ],
      });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useNextCountInventory() {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    (inventoryId: number) => nextCountInventory(inventoryId),
    {
      onSuccess() {
        queryClient.invalidateQueries(['inventories']);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useValidateInventory() {
  return useMutation(ValidateInventory, {
    onError(error) {
      notificationError(error);
    },
  });
}
